import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText,
  User,
  Activity,
  MapPin,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  ChevronRight,
  Stethoscope,
  ClipboardList,
  ShieldAlert
} from 'lucide-react';
import toast from 'react-hot-toast';

const DoctorReview = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState(null);
  const [decision, setDecision] = useState('');
  const [riskLevel, setRiskLevel] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchLeadData();
  }, [token]);

  const [fetchError, setFetchError] = useState(null);

  const fetchLeadData = async () => {
    try {
      const cleanToken = token ? token.trim() : '';
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/leads/public/review/${cleanToken}`);
      const leadData = response.data.data;
      setLead(leadData);

      if (leadData?.clinicalData?.doctorReviewDone) {
        setDecision(leadData.clinicalData.reviewDecision);
        setRemarks(leadData.clinicalData.reviewRemarks);
        setSubmitted(true);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching lead data:', error);
      setFetchError(error.message || 'Unknown error occurred');
      toast.error(error.response?.data?.message || 'Invalid or expired review link');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!decision) {
      toast.error('Please select a decision');
      return;
    }

    if (decision === 'Surgery Possible' && !riskLevel) {
      toast.error('Please select a risk level');
      return;
    }

    const finalRemarks = remarks;

    if ((decision === 'Surgery Not Possible' || decision === 'Additional Investigation Required') && (!remarks || remarks.trim().length < 5)) {
      toast.error('Please provide a mandatory clinical remark for your decision');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${import.meta.env.VITE_API_URL}/leads/public/review/${token}`, {
        decision,
        remarks: finalRemarks,
        riskLevel
      });
      setSubmitted(true);
      toast.success(decision === 'Surgery Possible' ? 'Surgery Approved successfully' : 'Case marked as Not Possible');
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4 text-[#143E78]"></div>
          <p className="text-slate-600 font-medium">Loading clinical case data...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center border border-slate-100">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Review Submitted</h2>
          <p className="text-slate-600 mb-8">
            Thank you, Dr. {lead?.clinicalData?.reviewingDoctorId?.user?.lastName || 'Surgeon'}. Your professional decision has been recorded and the clinical coordination team has been notified.
          </p>
          <div className="bg-slate-50 rounded-2xl p-4 text-left border border-slate-100 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-3 h-3 rounded-full ${decision === 'Surgery Possible' ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
              <span className="font-semibold text-slate-800">{decision}</span>
            </div>
            {remarks && (
              <p className="text-sm text-slate-500 italic">"{remarks}"</p>
            )}
          </div>
          <p className="text-xs text-slate-400">You can now close this window.</p>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center bg-gray-50">
        <ShieldAlert className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
        <p className="text-gray-600 max-w-md">
          This review link is either invalid, expired, or has already been used. Please contact the clinical coordination team for a new link.
        </p>
        {fetchError && (
          <div className="mt-4 p-4 bg-red-100 text-red-800 rounded-lg max-w-md w-full text-sm font-mono text-left break-words">
            <strong>DEBUG INFO:</strong><br />
            Error: {fetchError}<br />
            API URL: {import.meta.env.VITE_API_URL}<br />
            Token: {token}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#143E78] p-2 rounded-xl">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1E293B] leading-none mb-1">Doctor Portal</h1>
              <p className="text-xs text-slate-500 font-medium">Surgical Review Stage</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-100">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Secure Assessment</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left Column: Patient Info & Reports */}
          <div className="lg:col-span-2 space-y-6">

            {/* Patient Hero Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#143E78] to-[#1e5aa8] px-6 py-6 text-white">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="px-2 py-1 bg-white/20 backdrop-blur-md rounded text-[10px] font-bold uppercase tracking-wider mb-2 inline-block">Patient Case</span>
                    <h2 className="text-2xl font-bold">{lead.patientName}</h2>
                  </div>
                  <div className="text-right">
                    <p className="text-white/80 text-xs font-medium uppercase tracking-widest">Treatment</p>
                    <p className="font-bold">{lead.treatmentRequired || lead.disease || 'General Surgery'}</p>
                  </div>
                </div>
                <div className="flex gap-6 mt-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-white/60" />
                    <span className="text-sm font-medium">{lead.patientAge} Years • {lead.patientGender || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-white/60" />
                    <span className="text-sm font-medium">{lead.disease}</span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4" /> Clinical Context
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1 block">Presenting Symptoms</label>
                    <p className="text-slate-700 text-sm font-medium leading-relaxed bg-slate-50 p-3 rounded-xl min-h-[60px]">
                      {lead.symptoms || lead.medicalDetails || 'Review patient reports for detailed symptoms.'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1 block">Medical History</label>
                    <p className="text-slate-700 text-sm font-medium leading-relaxed bg-slate-50 p-3 rounded-xl min-h-[60px]">
                      {lead.clinicalIntake?.medicalDetails?.history?.pastDiseases || lead.clinicalIntake?.medicalDetails?.history?.currentIllness || 'None reported.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Patient Clinical History */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <Activity className="w-4 h-4" /> Detailed Medical History
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: 'Current Illness', value: lead.clinicalIntake?.medicalDetails?.history?.currentIllness },
                  { label: 'Previous Illness', value: lead.clinicalIntake?.medicalDetails?.history?.pastDiseases },
                  { label: 'Past Surgeries', value: lead.clinicalIntake?.medicalDetails?.history?.previousSurgeries },
                ].map((historyItem, i) => (
                  <div key={i} className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">{historyItem.label}</p>
                    <p className="text-sm font-medium text-slate-800 leading-relaxed">{historyItem.value || 'None reported'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Documents Section */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Diagnostic Reports
              </h3>

              {lead.documents && lead.documents.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {lead.documents.map((doc, idx) => (
                    <a
                      key={idx}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between p-4 bg-slate-50 hover:bg-[#143E78] transition-all duration-300 rounded-2xl border border-slate-100 hover:border-[#143E78]"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white group-hover:bg-white/20 rounded-xl flex items-center justify-center text-[#143E78] group-hover:text-white transition-colors shadow-sm">
                          <FileText className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-700 group-hover:text-white transition-colors truncate max-w-[120px]">
                            {doc.name || 'Medical Report'}
                          </p>
                          <p className="text-[10px] text-slate-400 group-hover:text-white/60 uppercase font-bold tracking-wider">
                            {doc.source || 'Clinical'} Upload
                          </p>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500">No medical reports available for digital review.</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Decision Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-0 overflow-hidden sticky top-28">
              <div className="bg-slate-900 px-6 py-5 text-white">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  Clinical Decision
                </h3>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 block">Evaluation Outcome</label>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setDecision('Surgery Possible')}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all duration-200 ${decision === 'Surgery Possible'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-900 shadow-md ring-4 ring-emerald-50'
                        : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-emerald-200'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${decision === 'Surgery Possible' ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                          {decision === 'Surgery Possible' && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </div>
                        <span className="font-bold">Fit For Surgery</span>
                      </div>
                    </button>

                    {decision === 'Surgery Possible' && (
                      <div className="pl-4 space-y-2 mb-4 animate-fade-in-down">
                        <p className="text-xs font-semibold text-slate-500 uppercase">Select Risk Level</p>
                        <div className="flex gap-2">
                          {['Mild Risk', 'Moderate Risk', 'Severe Risk'].map((level) => (
                            <label
                              key={level}
                              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer transition-all ${riskLevel === level
                                ? 'bg-emerald-500 text-white border-emerald-500 font-medium'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50'
                                }`}
                            >
                              <input
                                type="radio"
                                name="riskLevel"
                                value={level}
                                checked={riskLevel === level}
                                onChange={(e) => setRiskLevel(e.target.value)}
                                className="hidden"
                              />
                              {level}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => { setDecision('Surgery Not Possible'); setRiskLevel(''); }}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all duration-200 ${decision === 'Surgery Not Possible'
                        ? 'border-rose-500 bg-rose-50 text-rose-900 shadow-md ring-4 ring-rose-50'
                        : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-rose-200'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${decision === 'Surgery Not Possible' ? 'border-rose-500 bg-rose-500' : 'border-slate-300'}`}>
                          {decision === 'Surgery Not Possible' && <XCircle className="w-4 h-4 text-white" />}
                        </div>
                        <span className="font-bold">Unfit For Surgery</span>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setDecision('Additional Investigation Required'); setRiskLevel(''); }}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all duration-200 ${decision === 'Additional Investigation Required'
                        ? 'border-amber-500 bg-amber-50 text-amber-900 shadow-md ring-4 ring-amber-50'
                        : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-amber-200'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${decision === 'Additional Investigation Required' ? 'border-amber-500 bg-amber-500' : 'border-slate-300'}`}>
                          {decision === 'Additional Investigation Required' && <AlertCircle className="w-4 h-4 text-white" />}
                        </div>
                        <span className="font-bold">Additional Investigation</span>
                      </div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex gap-1 items-center">
                    Clinical Remarks {(decision === 'Surgery Not Possible' || decision === 'Additional Investigation Required') && <span className="text-rose-500">*</span>}
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder={
                      decision === 'Surgery Not Possible'
                        ? "Please provide solid medical reasons for rejection..."
                        : decision === 'Additional Investigation Required'
                          ? "What specific tests or reports are needed?"
                          : "Provide specific reasons or requirements for your decision..."
                    }
                    className={`w-full h-32 bg-slate-50 border rounded-2xl p-4 text-sm focus:outline-none focus:ring-4 transition-all ${(decision === 'Surgery Not Possible' || decision === 'Additional Investigation Required') && !remarks
                      ? 'border-rose-200 focus:ring-rose-500/10 focus:border-rose-500'
                      : 'border-slate-200 focus:ring-[#143E78]/10 focus:border-[#143E78]'
                      }`}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submitting || !decision}
                  className="w-full bg-[#143E78] hover:bg-[#1E293B] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 rounded-2xl transition-all duration-300 shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Professional Decision</span>
                      <ChevronRight className="w-5 h-5" />
                    </>
                  )}
                </button>
                <p className="text-[10px] text-slate-400 text-center px-4 leading-relaxed font-medium">
                  By submitting, you confirm that you have reviewed all available reports and this decision represents your professional clinical judgement.
                </p>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-200 mt-12 bg-white">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#143E78]">DOXEZ</span>
            <span className="text-sm text-slate-400">Secure Surgeon Protocol Link • v2.0</span>
          </div>
          <div className="flex gap-8">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Medical Accuracy</span>
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Secure Handling</span>
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">HIPAA Compliant</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DoctorReview;
